import { CopyIcon, EllipsisVerticalIcon, LoaderCircleIcon, PencilIcon, RotateCwIcon, Trash2Icon } from 'lucide-react'

import { Badge, DropdownMenu, IconButton, Tooltip } from 'slab'
import { getFormattedDate } from 'utils'
import {
  type ResourceAccessTokensResourceAccessTokenAbilitiesItem,
  type ResourceAccessTokensResourceAccessTokenRecipient as ResourceAccessTokenRecipient,
  ResourceAccessTokensResourceAccessTokenStatus as ResourceAccessTokenStatus,
} from 'utils/web_api.schemas'

type TokenRecipientRowProps = {
  recipient: ResourceAccessTokenRecipient,
  onResendInvite: (abilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]) => void,
  onCopyLink: (tokenId: string | null, recipient: ResourceAccessTokenRecipient) => void,
  onEditPermissions: (recipientId: string, abilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]) => void,
  onRevokeAccess: (recipientId: string) => void,
  isInviting: boolean,
  isCopyingLink: boolean,
  isRevoking: boolean,
  canEditQuote: boolean,
  canSendEditorInvites: boolean,
  canSendOnboardInvites: boolean
}

export const TokenRecipientRow = ({
  recipient,
  onResendInvite,
  onCopyLink,
  onEditPermissions,
  onRevokeAccess,
  isInviting,
  isCopyingLink,
  isRevoking,
  canEditQuote,
  canSendEditorInvites,
  canSendOnboardInvites,
}: TokenRecipientRowProps) => {
  // User can only edit permissions if they have at least one permission they can modify
  const canEditPermissions = canEditQuote && (canSendEditorInvites || canSendOnboardInvites)

  if (recipient.tokens.length === 0) {
    // Should never happen
    return null
  }

  // Recipient can have multiple tokens, if they have a redeemed token we consider that the recipient has accepted the invitation
  const redeemedToken = recipient.tokens.find(token => token.status === ResourceAccessTokenStatus.redeemed)
  const mostRecentRedeemableToken = recipient.tokens.filter(token =>
    token.status === ResourceAccessTokenStatus.unredeemed
  ).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]

  // we collect all abilities from valid tokens in order to determine the abilities to grant when resending an invite
  // and also to display the cumulative permissions the recipient has
  const cumulativeAbilities = Array.from(
    new Set(
      recipient.tokens
        .filter(token => token.status === ResourceAccessTokenStatus.redeemed || token.status === ResourceAccessTokenStatus.unredeemed)
        .flatMap(token => token.abilities)
    )
  )

  // Display cumulative abilities from all valid tokens, not just one token
  const abilitiesToDisplay = cumulativeAbilities

  const tokenToCopy = mostRecentRedeemableToken || redeemedToken

  return (
    <div key={recipient.id} className='flex items-center justify-between px-2 py-4 hover:bg-gray-50 transition-colors'>
      <div className='font-semibold text-sm flex items-center space-x-2'>
        <span>{recipient.email}</span>
        {isInviting && <LoaderCircleIcon className='w-4 h-4 animate-spin' />}
      </div>
      <div className='flex items-center space-x-2 cursor-default'>
        {abilitiesToDisplay.length > 0 && (
          <Tooltip content={`${recipient.email} has permission to ${abilitiesToDisplay.includes('edit') ? 'edit' : 'view'} the quote${abilitiesToDisplay.includes('onboard') ? ' and onboard to Thatch from it' : ''}.`} withPortal={false}>
            <div className='text-sm text-gray-500 whitespace-nowrap'>
              {abilitiesToDisplay.includes('edit') ? 'Editor' : 'Viewer'}
              {abilitiesToDisplay.includes('onboard') && ', Can onboard'}
            </div>
          </Tooltip>
        )}
        {redeemedToken && redeemedToken.redeemed_at && (
          <Tooltip content={`Accepted invitation on ${getFormattedDate(redeemedToken.redeemed_at)}${redeemedToken.redeemed_by_geo_location ? ` in ${redeemedToken.redeemed_by_geo_location}` : ''}.`} withPortal={false}>
            <Badge type='success'>
              Reviewing quote
            </Badge>
          </Tooltip>
        )}
        {!redeemedToken && mostRecentRedeemableToken && (
          <Tooltip content={`Waiting to accept their invitation sent on ${getFormattedDate(mostRecentRedeemableToken.created_at)}.`} withPortal={false}>
            <Badge type='info'>
              Pending
            </Badge>
          </Tooltip>
        )}
        {!redeemedToken && !mostRecentRedeemableToken && (
          // If they don't have a redeemed token or a redeemable token, they are either expired or revoked
          <Badge type='critical'>
            Expired
          </Badge>
        )}
        {canEditQuote && (
          <IconButton
            icon={isCopyingLink ? LoaderCircleIcon : CopyIcon}
            label='Copy link'
            onClick={() => onCopyLink(tokenToCopy?.id ?? null, recipient)}
            disabled={isCopyingLink}
          />
        )}
        <DropdownMenu
          trigger={<IconButton icon={EllipsisVerticalIcon} label='More' />}
          withPortal={false}
        >
          <DropdownMenu.Item
            leftIcon={RotateCwIcon}
            onSelect={() => onResendInvite(cumulativeAbilities)}
          >
            Resend invite
          </DropdownMenu.Item>
          {canEditPermissions && (
            <DropdownMenu.Item
              leftIcon={PencilIcon}
              onSelect={() => onEditPermissions(recipient.id, cumulativeAbilities)}
            >
              Edit permissions
            </DropdownMenu.Item>
          )}
          {canEditQuote && (
            <DropdownMenu.Item
              leftIcon={isRevoking ? LoaderCircleIcon : Trash2Icon}
              onSelect={() => onRevokeAccess(recipient.id)}
              disabled={isRevoking}
            >
              {isRevoking ? 'Revoking...' : 'Revoke access'}
            </DropdownMenu.Item>
          )}
        </DropdownMenu>
      </div>
    </div>
  )
}
